/**
 * Utility per la gestione del Codice Fiscale italiano
 * Parsing, validazione e estrazione dati (data/luogo nascita)
 */

import api from '../services/api';

// Mappatura mesi nel codice fiscale
const MONTH_CODES = {
  'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'H': 6,
  'L': 7, 'M': 8, 'P': 9, 'R': 10, 'S': 11, 'T': 12
};

// Tabelle per validazione checksum
const ODD_VALUES = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23
};

const EVEN_VALUES = {
  '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
  'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25
};

const CHECK_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Valida il formato del codice fiscale (solo pattern, no checksum)
 * @param {string} cf - Codice fiscale
 * @returns {boolean}
 */
export function isValidFormat(cf) {
  if (!cf || cf.length !== 16) return false;
  const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9A-Z]{3}[A-Z]$/i;
  return pattern.test(cf);
}

/**
 * Valida il codice fiscale completo (incluso checksum)
 * @param {string} cf - Codice fiscale
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateCodiceFiscale(cf) {
  if (!cf || cf.length !== 16) {
    return { valid: false, error: 'Il codice fiscale deve essere di 16 caratteri.' };
  }
  
  const cfUpper = cf.toUpperCase();
  
  // Verifica pattern
  const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9A-Z]{3}[A-Z]$/;
  if (!pattern.test(cfUpper)) {
    return { valid: false, error: 'Formato codice fiscale non valido.' };
  }
  
  // Calcola checksum
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const char = cfUpper.charAt(i);
    if (i % 2 === 0) {
      sum += ODD_VALUES[char];
    } else {
      sum += EVEN_VALUES[char];
    }
  }
  
  const expectedCheckChar = CHECK_CHARS.charAt(sum % 26);
  const actualCheckChar = cfUpper.charAt(15);
  
  if (expectedCheckChar !== actualCheckChar) {
    return { 
      valid: false, 
      error: `Carattere di controllo non valido.` 
    };
  }
  
  return { valid: true };
}

/**
 * Estrae i dati dal codice fiscale (solo parsing locale, senza API)
 * @param {string} cf - Codice fiscale
 * @returns {{ birthDate: string, codiceCatastale: string, gender: string } | null}
 */
export function parseCodiceFiscaleLocal(cf) {
  if (!isValidFormat(cf)) return null;
  
  const cfUpper = cf.toUpperCase();
  
  // Anno (posizioni 6-7)
  const yearCode = parseInt(cfUpper.substring(6, 8));
  const fullYear = yearCode > 30 ? 1900 + yearCode : 2000 + yearCode;
  
  // Mese (posizione 8)
  const monthChar = cfUpper.charAt(8);
  const month = MONTH_CODES[monthChar];
  if (!month) return null;
  
  // Giorno (posizioni 9-10)
  let day = parseInt(cfUpper.substring(9, 11));
  const isFemale = day > 40;
  if (isFemale) day -= 40;
  
  // Codice catastale (posizioni 11-14)
  const codiceCatastale = cfUpper.substring(11, 15);
  
  // Formatta data YYYY-MM-DD
  const birthDate = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  
  return {
    birthDate,
    birthYear: fullYear,
    birthMonth: month,
    birthDay: day,
    gender: isFemale ? 'F' : 'M',
    codiceCatastale
  };
}

/**
 * Estrae i dati dal codice fiscale con lookup luogo di nascita via API
 * @param {string} cf - Codice fiscale
 * @returns {Promise<object>}
 */
export async function parseCodiceFiscale(cf) {
  // Validazione locale prima
  const validation = validateCodiceFiscale(cf);
  if (!validation.valid) {
    return { valid: false, error: validation.error };
  }
  
  // Parsing locale
  const localData = parseCodiceFiscaleLocal(cf);
  if (!localData) {
    return { valid: false, error: 'Errore nel parsing del codice fiscale.' };
  }
  
  // Lookup luogo di nascita via API
  try {
    const response = await api.get(`/comuni/codice-catastale/${localData.codiceCatastale}`);
    
    return {
      valid: true,
      codiceFiscale: cf.toUpperCase(),
      birthDate: localData.birthDate,
      birthYear: localData.birthYear,
      birthMonth: localData.birthMonth,
      birthDay: localData.birthDay,
      gender: localData.gender,
      codiceCatastale: localData.codiceCatastale,
      birthPlace: response.data.nome || null,
      birthPlaceType: response.data.tipo || 'comune_italiano',
      birthPlaceFound: !!response.data.nome
    };
  } catch (error) {
    // Se l'API fallisce, ritorna comunque i dati locali
    return {
      valid: true,
      codiceFiscale: cf.toUpperCase(),
      birthDate: localData.birthDate,
      birthYear: localData.birthYear,
      birthMonth: localData.birthMonth,
      birthDay: localData.birthDay,
      gender: localData.gender,
      codiceCatastale: localData.codiceCatastale,
      birthPlace: null,
      birthPlaceType: localData.codiceCatastale.startsWith('Z') ? 'stato_estero' : 'comune_italiano',
      birthPlaceFound: false,
      apiError: true
    };
  }
}

/**
 * Cerca comuni per nome (autocomplete)
 * @param {string} query - Testo da cercare
 * @returns {Promise<Array>}
 */
export async function searchComuni(query) {
  if (!query || query.length < 2) return [];
  
  try {
    const response = await api.get('/comuni/search', { params: { q: query } });
    return response.data.results || [];
  } catch (error) {
    console.error('Errore ricerca comuni:', error);
    return [];
  }
}

const codiceFiscaleUtils = {
  isValidFormat,
  validateCodiceFiscale,
  parseCodiceFiscaleLocal,
  parseCodiceFiscale,
  searchComuni
};

export default codiceFiscaleUtils;
